import React from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import { CardConfidence, CardDataState } from 'containers/Intelligence/types';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import CardDataStateBlock from '../CardDataStateBlock';
import CardFreshnessStamp, { CardFreshnessLabel } from '../CardFreshnessStamp';
import CardInfoTooltip, { CardTooltipContent } from '../CardInfoTooltip';
import ConfidenceBadge from '../ConfidenceBadge';
import ConfidenceReason from '../ConfidenceReason';

interface IInsightCardBaseProps {
  title: string;
  subtitle?: string;
  /**
   * The section band, drawn above the card title INSIDE this card's own frame. 589-29260 is one
   * 1360x731 surface holding the band title at y=14, the band description at y=54 and the card title
   * at y=104, so the band is not a separate panel above the card. Placed in the title group rather
   * than above the header so the confidence badge lands level with the band title, at y=20.2, which
   * is where the same node draws it. Omitted, the card is exactly what it was.
   */
  band?: React.ReactNode;
  /** FR: every card title carries an approved explainer, so this is not optional. */
  tooltip: CardTooltipContent;
  state: CardDataState;
  /** `null` when the payload carries no calculation instant; the stamp is then omitted. */
  updatedAt: string | null;
  freshnessLabel?: CardFreshnessLabel;
  /**
   * Omit entirely for a card the FR does not score. Pass `null` for a scored card whose payload
   * carries no result: that renders the `insufficient` badge, because an unscored card has to say so
   * rather than silently drop the pill.
   */
  confidence?: CardConfidence | null;
  onViewDetails?: () => void;
  /** Slot for the footer call-to-action row (Growth Planner handoff). */
  cta?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  /**
   * PLA-8828: the card shell, as five optional overrides that each default to the value this card
   * has always drawn. Customer 360 draws its cards on a second frame whose reason column and
   * badge-to-reason gap differ from Layer 1's (`3583-10327` measures 214, `3583-10660` 251.65, and
   * the badge-to-reason gap reads 6.8 on `3583-10094`), and its shell may yet want a border the
   * Layer-1 card does not draw. An unpassed prop leaves the 27 live Layer-1 cards on today's pixels.
   */
  padding?: string;
  radius?: string;
  background?: string;
  border?: string;
  /** Width of the confidence-reason column under the badge. */
  reasonMeasure?: string;
  /** Gap between the confidence badge and the reason beneath it. */
  badgeToReasonGap?: string;
  /**
   * Draws the state's own sentence under the marker in the default state block. OFF by default so
   * Layer 1's 27 shipped cards keep the label alone; Customer 360 turns it on. PLA-8828.
   */
  stateDescription?: boolean;
}

/**
 * The two ways to fill the body of a degraded card, and they are mutually exclusive by type: the card
 * cannot reach into an opaque `stateBlock` to hand it `onRetry`, so accepting both would let a caller
 * wire a retry that nothing can ever call. An override owns its own affordances.
 */
type InsightCardStateBlockProps =
  | {
      stateBlock?: undefined;
      /** Wired by the owner of the fetch; reaches the default state block's Retry affordance. */
      onRetry?: () => void;
    }
  | {
      /**
       * Replaces the body while `state.kind !== 'ok'`. Omitted, the card renders the default
       * `CardDataStateBlock` for its own `state`, so a degraded card can never fall through to an
       * empty body.
       */
      stateBlock: React.ReactNode;
      onRetry?: never;
    };

export type IInsightCardProps = IInsightCardBaseProps & InsightCardStateBlockProps;

const DEFAULT_PADDING = `${tokens.space['20']} ${tokens.space['24']}`;
const DEFAULT_RADIUS = tokens.radius.lg;
const DEFAULT_BACKGROUND = tokens.color['surface-section'];
// The Layer-1 card draws no border at all; `none` is that, expressed as a value a caller can replace.
const DEFAULT_BORDER = 'none';

interface IShellProps {
  $padding: string;
  $radius: string;
  $background: string;
  $border: string;
}

const Card = styled.article<IShellProps>`
  display: flex;
  flex-direction: column;
  padding: ${({ $padding }) => $padding};
  border: ${({ $border }) => $border};
  border-radius: ${({ $radius }) => $radius};
  background: ${({ $background }) => $background};

  /* Margins rather than flex gap (cross-browser § 5), and deliberately no overflow rule: the
     explainer's 618px panel is portalled into this subtree and clipping it would hide it. */
  > * + * {
    margin-top: ${tokens.space['4']};
  }
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  > * + * {
    margin-left: ${tokens.space['16']};
  }
`;

const TitleGroup = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;

  /* The doubled class outranks the subtitle's own margin reset, which is injected later and would
     otherwise win the cascade on equal specificity and collapse this gap to nothing. */
  && > * + * {
    margin-top: ${tokens.space['4']};
  }
`;

/* `Group 40450.svg`, all three scored cards: the block's right edge is the card's content-box right
   edge — card 2's first line inks to 1335.74 against a 1336 edge — and its widest line runs 244.14,
   so 245 is the tightest measure the mockup's longest line fits in. Deliberately NOT a share of the
   card: the 1360-wide card widens the block to 275 against the 666-wide card's 245, a 12% gain on a
   card twice as wide, so a proportion would balloon it on the full-width rows. */
const REASON_MEASURE = '245px';

/* Figma stacks the confidence reason under the badge/trigger pair rather than at the foot of the
   card, so the whole meta group is a right-anchored column. Sizing the group (not the reason) is
   what keeps the badge and trigger pinned to the content edge while the reason gets its own
   measure. `min-content` floors it at the badge row, which cannot wrap; the percentage cap keeps a
   long reason from wrapping the title instead of itself once a fluid card is narrower than 545. */
// The measure exists to give the reason its own column; a card with no reason claims only what the
// badge row needs, or it would hold 245px of empty width off the title for a line that never draws.
const HeaderMeta = styled.div<{ $hasReason: boolean; $measure: string }>`
  display: flex;
  width: ${({ $hasReason, $measure }) => ($hasReason ? $measure : 'auto')};
  min-width: min-content;
  max-width: 45%;
  flex-direction: column;
  flex-shrink: 0;
  align-items: flex-end;
`;

/* Kept in flow rather than absolutely positioned so the trigger can never land on top of the badge. */
const HeaderMetaRow = styled.div`
  display: flex;
  align-items: flex-start;

  > * + * {
    margin-left: ${tokens.space['12']};
  }
`;

// Measured badge-bottom to reason-top on both scored cards of `Group 40450.svg`: 9.8px and 10.9px.
// Off the 4px space scale, so it stays a literal rather than being rounded onto a token.
const BADGE_TO_REASON_GAP = '10px';

/* Zero-width plus a percentage minimum: the reason contributes nothing to the group's intrinsic
   width, so the group keeps the measure declared above instead of being stretched by a long reason,
   and the reason then fills that measure rather than collapsing onto the badge row's width.

   The bottom margin restates the card's own header-to-body step under the reason. On a scored card
   the reason is the last line of the header's tallest column, so that single 4px step was the only
   thing between a 10px caption and the body block - measured flush at 4.0px on 9 of the 20 Customer
   Insights cards (recheck50). A card with no reason never renders this element and is unchanged.
   PLA-8844. */
const HeaderReason = styled(ConfidenceReason)<{ $gap: string }>`
  width: 0;
  min-width: 100%;
  margin-top: ${({ $gap }) => $gap};
  margin-bottom: ${tokens.space['4']};
`;

const Title = styled.h3`
  margin: 0;
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['card-header-size']};
  font-weight: ${tokens.type['card-header-weight']};
  line-height: ${tokens.type['card-header-line']};
`;

const Subtitle = styled.p`
  margin: 0;
  color: ${tokens.color['text-caption']};
  font-size: ${tokens.type['card-description-size']};
  font-weight: ${tokens.type['card-description-weight']};
  line-height: ${tokens.type['card-description-line']};
`;

/* The freshness stamp sits in the card's bottom-right corner, on its own line under the action row
   rather than beside it, so the Growth Planner CTA and the stamp share that corner without
   colliding (`Group 40450.svg` y616 of a card ending at y639; figma-live-read-2026-08-11.md § 4). */
const Footer = styled.footer`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  /* PLA-8815: the margin-top:auto below is FREE space, and the tallest card of a row has none to
     absorb — it resolved to 0 there and the footer sat flush on the body (measured 0.0px on the
     payment-behaviour card in both engines at 1440 and 1280, against 147px on its shorter neighbour,
     which is a height difference rather than a designed gap). Padding is space in every case. The
     file reads 10px (589-28939 top 604.8 over the evidence frame's bottom 594.8), off the
     4/8/12/16/20/24/32 scale; 8 is the nearest step. */
  padding-top: ${tokens.space['8']};

  > * + * {
    margin-top: ${tokens.space['8']};
  }

  /* Doubled to outrank the card's own child-spacing rule. A card stretched by a taller neighbour
     would otherwise end its footer where its content ends, leaving the stamp mid-card rather than in
     the corner — Figma pins it to the card's bottom edge whatever the content height. */
  && {
    margin-top: auto;
  }
`;

const FooterActions = styled.div`
  display: flex;
  align-self: stretch;
  align-items: center;
  justify-content: flex-end;

  > * + * {
    margin-left: ${tokens.space['12']};
  }
`;

/* PLA-8815: the handoff strips three bands pass here declare `width: 100%`, which was resolving
   against a shrink-wrapped parent — a flex item is content-sized unless told to grow, so the strip
   drew 305.5px of a 640px row where `589-28939` draws 624 of 624. The slot grows and right-aligns
   its content, so a strip fills the card's content column and a bare CTA button, which four other
   bands pass, stays exactly where it was. */
const CtaSlot = styled.div`
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  justify-content: flex-end;
`;

const ViewDetailsButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${tokens.color['accent-blue']};
  cursor: pointer;
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

// Forwards its ref to the root `<article>` — the intersection-observer gate a caller lazy-loads
// this card's data behind (`hooks/useInViewport`) has to observe the actual rendered box, and this
// is the only element in the tree that is one across every state the card can be in.
const InsightCard = React.forwardRef<HTMLElement, IInsightCardProps>(
  (
    {
      title,
      subtitle,
      band,
      tooltip,
      state,
      updatedAt,
      freshnessLabel,
      confidence,
      stateBlock,
      onRetry,
      onViewDetails,
      cta,
      children,
      className,
      padding = DEFAULT_PADDING,
      radius = DEFAULT_RADIUS,
      background = DEFAULT_BACKGROUND,
      border = DEFAULT_BORDER,
      reasonMeasure = REASON_MEASURE,
      badgeToReasonGap = BADGE_TO_REASON_GAP,
      stateDescription = false
    },
    ref
  ) => {
    const { t } = useMultiLanguageTranslation();

    // A card in any non-`ok` state keeps its heading and swaps only the body: stripping the heading
    // too would read as a different card rather than a degraded one. The default block is rendered
    // in-frame so a degraded card cannot end up with no explanation at all.
    const body =
      state.kind === 'ok'
        ? children
        : stateBlock || (
            <CardDataStateBlock state={state} onRetry={onRetry} describes={stateDescription} />
          );

    // A card with no data behind it — denied, or failed outright — must not keep advertising a route
    // into that data or a freshness claim about it. `loading` is deliberately not in this set: its
    // footer treatment is still undesigned (PLA-8815 plan.md AF-2).
    const hasNoDataBehindIt =
      state.kind === 'permissionRestricted' ||
      state.kind === 'consentRestricted' ||
      state.kind === 'technicalError';
    const showsFreshness = Boolean(updatedAt) && !hasNoDataBehindIt;
    const showsViewDetails = Boolean(onViewDetails) && !hasNoDataBehindIt;
    const showsCta = Boolean(cta) && !hasNoDataBehindIt;
    const hasFooterActions = showsViewDetails || showsCta;
    const hasFooter = showsFreshness || hasFooterActions;

    // Same rule as the footer claims above: a card with no data behind it publishes no confidence
    // score either, whatever the caller passed.
    const showsConfidence = confidence !== undefined && !hasNoDataBehindIt;
    const hasReason = showsConfidence && Boolean(confidence?.reasons?.length);

    return (
      <Card
        ref={ref}
        $background={background}
        $border={border}
        $padding={padding}
        $radius={radius}
        className={className}
        aria-label={title}
        data-state={state.kind}
        data-testid="insight-card"
      >
        <Header data-testid="insight-card-header">
          <TitleGroup>
            {band}
            <Title data-testid="insight-card-title">{title}</Title>
            {subtitle ? <Subtitle data-testid="insight-card-subtitle">{subtitle}</Subtitle> : null}
          </TitleGroup>

          <HeaderMeta
            $hasReason={hasReason}
            $measure={reasonMeasure}
            data-testid="insight-card-meta"
          >
            <HeaderMetaRow>
              {showsConfidence ? (
                <div data-testid="insight-card-confidence">
                  <ConfidenceBadge
                    band={confidence ? confidence.band : 'insufficient'}
                    score={confidence ? confidence.score : null}
                  />
                </div>
              ) : null}

              <CardInfoTooltip title={title} content={tooltip} />
            </HeaderMetaRow>

            {/* Tied to the pill, not merely to the payload: a card whose score is withheld must not
              keep publishing the reasoning behind it. An empty list renders nothing at all. */}
            {showsConfidence ? (
              <HeaderReason
                $gap={badgeToReasonGap}
                reasons={confidence ? confidence.reasons : []}
              />
            ) : null}
          </HeaderMeta>
        </Header>

        {body ? <div data-testid="insight-card-body">{body}</div> : null}

        {hasFooter ? (
          <Footer data-testid="insight-card-footer">
            {hasFooterActions ? (
              <FooterActions>
                {showsViewDetails ? (
                  <ViewDetailsButton
                    type="button"
                    onClick={onViewDetails}
                    aria-label={t(`${TCustomerInsights}.Actions.ViewDetailsFor`, { label: title })}
                    data-testid="insight-card-view-details"
                  >
                    {t(`${TCustomerInsights}.Actions.ViewDetails`)}
                  </ViewDetailsButton>
                ) : null}

                {showsCta ? <CtaSlot data-testid="insight-card-cta">{cta}</CtaSlot> : null}
              </FooterActions>
            ) : null}

            {showsFreshness ? (
              <CardFreshnessStamp calculatedAt={updatedAt} label={freshnessLabel} />
            ) : null}
          </Footer>
        ) : null}
      </Card>
    );
  }
);

InsightCard.displayName = 'InsightCard';

export default InsightCard;
